import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './Login.css';

const Login = () => {
  const [credentials, setCredentials] = useState({
    mobile: '',
    password: '',
    device_id: 'web-browser-' + Date.now()
  });
  const [loading, setLoading] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check if already logged in on component mount
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Fetch contact settings on component mount
  useEffect(() => {
    fetchContactSettings();
  }, []);

  const fetchContactSettings = async () => {
    setContactLoading(true);
    try {
      const response = await fetch('https://playinbyd.com/api/admin/get-contact-settings');
      const data = await response.json();
      
      if (data.success) {
        setContactSettings(data.contact_data);
      } else {
        console.error('Failed to fetch contact settings');
      }
    } catch (error) {
      console.error('Error fetching contact settings:', error);
    } finally {
      setContactLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!credentials.mobile || !credentials.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (credentials.mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    // Check if password meets minimum length
    if (credentials.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      const result = await login(credentials);
      
      if (result.success) {
        // Show success message from API or default
        toast.success(result.data?.msg || 'Login successful!');
        
        // Add a small delay to ensure state is updated
        setTimeout(() => {
          // Double-check authentication before redirecting
          if (isAuthenticated || localStorage.getItem('authToken')) {
            navigate('/', { replace: true });
          }
        }, 500);
      } else {
        // Show error message from API or default
        toast.error(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize inputs
    if (name === 'mobile') {
      // Allow only numbers, max 10 digits
      const numbersOnly = value.replace(/\D/g, '').slice(0, 10);
      setCredentials({ ...credentials, [name]: numbersOnly });
    } else if (name === 'password') {
      // Trim spaces from password
      setCredentials({ ...credentials, [name]: value.trim() });
    } else {
      setCredentials({ ...credentials, [name]: value });
    }
  };

  // Pre-fill test credentials for development (remove in production)
  const fillTestCredentials = () => {
    setCredentials({
      mobile: '9482345952',
      password: '123456',
      device_id: 'web-browser-' + Date.now()
    });
    toast('Test credentials filled. Click LOGIN to test.');
  };

  return (
    <div className="login-container">
      {/* Contact Icon */}
      <div className="contact-icon-container">
        <button 
          className="contact-icon-btn"
          onClick={() => setShowContactInfo(!showContactInfo)}
          title="Contact Support"
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M20 10.9999C20 16.052 16.052 19.9999 11 19.9999C9.85933 19.9999 8.76989 19.8159 7.75456 19.4619L4 20.9999L5.63466 17.5322C5.22198 16.5233 5 15.4161 5 14.2499C5 9.19778 8.948 5.24988 14 5.24988C16.179 5.24988 18.173 6.03388 19.716 7.28688C20.486 7.88988 21.153 8.59888 21.686 9.39388" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M14 14.25V14.26" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
            />
            <path 
              d="M10 14.25V14.26" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
            />
          </svg>
          <span className="contact-icon-badge">?</span>
        </button>
      </div>

      {/* Contact Info Modal/Dropdown */}
      {showContactInfo && (
        <div className="contact-info-panel">
          <div className="contact-info-header">
            <h3>Contact Support</h3>
            <button 
              className="close-btn"
              onClick={() => setShowContactInfo(false)}
            >
              ×
            </button>
          </div>
          
          {contactLoading ? (
            <div className="contact-loading">
              <div className="spinner"></div>
              <p>Loading contact information...</p>
            </div>
          ) : contactSettings ? (
            <div className="contact-info-content">
              {/* Phone Numbers */}
              {contactSettings.mobile_1 && (
                <a href={`tel:${contactSettings.mobile_1}`} className="contact-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8 10a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" strokeWidth="1.5"/>
                  </svg>
                  <span>{contactSettings.mobile_1}</span>
                </a>
              )}

              {/* WhatsApp */}
              {contactSettings.whatsapp_no && (
                <a href={`https://wa.me/${contactSettings.whatsapp_no}`} target="_blank" rel="noopener noreferrer" className="contact-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" strokeWidth="1.5"/>
                  </svg>
                  <span>WhatsApp</span>
                </a>
              )}

              {/* Telegram */}
              {contactSettings.telegram_no && (
                <a href={`https://t.me/${contactSettings.telegram_username || contactSettings.telegram_no}`} target="_blank" rel="noopener noreferrer" className="contact-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21.5 4.5L2.5 11.5L8.5 14L18.5 7.5L11.5 15.5L15.5 20.5L21.5 4.5Z" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                  <span>Telegram</span>
                </a>
              )}

              {/* Email */}
              {(contactSettings.email_1 || contactSettings.email_2) && (
                <a href={`mailto:${contactSettings.email_1 || contactSettings.email_2}`} className="contact-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeWidth="1.5"/>
                    <polyline points="22,6 12,13 2,6" strokeWidth="1.5"/>
                  </svg>
                  <span>{contactSettings.email_1 || contactSettings.email_2}</span>
                </a>
              )}

              {/* Address */}
              {contactSettings.address && (
                <div className="contact-item address">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeWidth="1.5"/>
                    <circle cx="12" cy="10" r="3" strokeWidth="1.5"/>
                  </svg>
                  <span>{contactSettings.address}</span>
                </div>
              )}

            
            </div>
          ) : (
            <div className="contact-error">
              <p>Unable to load contact information</p>
            </div>
          )}
        </div>
      )}

      <div className="login-card">
        {/* LOGO SECTION */}
        <div className="login-logo">
          <div className="logo-container">
            {/* Add your logo from public folder */}
            <img 
              src="/logo1.png"  // Path to your logo in public folder
              alt="BetPro Logo"
              className="logo-image"
              onError={(e) => {
                // Fallback if logo doesn't exist
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML = `
                  <div class="logo-fallback">
                    <div style="
                      width: 80px;
                      height: 80px;
                      background: var(--gradient-primary);
                      border-radius: 12px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 32px;
                      color: white;
                      font-weight: bold;
                      box-shadow: var(--shadow-soft);
                    ">
                      BP
                    </div>
                  </div>
                `;
              }}
            />
          </div>
        </div>

        <div className="login-header">
          <h2>User Login</h2>
          
          {/* Development helper - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <button 
              onClick={fillTestCredentials}
              className="test-credentials-btn"
              type="button"
            >
              Fill Test Credentials
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="mobile">
              Mobile Number <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={credentials.mobile}
              onChange={handleChange}
              placeholder="Enter 10-digit mobile number"
              maxLength="10"
              pattern="[0-9]{10}"
              inputMode="numeric"
              autoComplete="tel"
              required
              disabled={loading}
              style={{ color: 'black' }}  /* Ensure text is visible */
            />
            <div className="input-hint">10 digits only</div>
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password <span className="required">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="Enter your password"
              minLength="6"
              autoComplete="current-password"
              required
              disabled={loading}
              style={{ color: 'black' }}  /* Ensure text is visible */
            />
            <div className="input-hint">Minimum 6 characters</div>
          </div>

          <button 
            type="submit" 
            className={`login-btn ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? 'LOGGING IN...' : 'LOGIN'}
          </button>

          <div className="divider">
            <span>OR</span>
          </div>

          <Link to="/register" className="register-link">
            <button 
              type="button" 
              className="register-btn"
              disabled={loading}
            >
              CREATE NEW ACCOUNT
            </button>
          </Link>

          <div className="remember-forgot">
            <label className="checkbox-label">
              <input type="checkbox" name="remember" />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" className="forgot-link">
              Forgot Password?
            </Link>
          </div>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="footer-link">
              Register here
            </Link>
          </p>
          <p>
            <a href="/playinbyd.apk" className="footer-link">
              Download App
            </a>
          </p>
          <p className="terms">
            By logging in, you agree to our{' '}
            <Link to="/">Terms of Service</Link> and{' '}
            <Link to="/">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;